import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    FormControlLabel,
    Switch
} from '@mui/material';
import { useSnackbar } from 'notistack';
import sequenceAPI from '../../api/sequence';

const SequenceManagement: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState<any>(null);
    const [formData, setFormData] = useState({
        prefix_template: '',
        next_sequence: 1,
        padding: 5,
        auto_reset_yearly: true
    });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await sequenceAPI.getModuleSettings('sales_order');
            setSettings(data);
            setFormData({
                prefix_template: data.prefix_template,
                next_sequence: data.next_sequence,
                padding: data.padding,
                auto_reset_yearly: data.auto_reset_yearly
            });
        } catch (error) {
            console.error('Failed to load settings:', error);
            enqueueSnackbar('Failed to load sequence settings', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await sequenceAPI.updateSettings('sales_order', formData);
            enqueueSnackbar('Sequence settings updated successfully', { variant: 'success' });
            loadSettings();
        } catch (error) {
            console.error('Failed to update settings:', error);
            enqueueSnackbar('Failed to update settings', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Sequence Number Management
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {settings?.display_name || 'Sales Order'} Settings
                        </Typography>

                        <Alert severity="info" sx={{ mb: 3 }}>
                            Next SO will be: <strong>{settings?.current_prefix}{String(formData.next_sequence).padStart(formData.padding, '0')}</strong>
                        </Alert>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Prefix Template"
                                value={formData.prefix_template}
                                onChange={(e) => handleChange('prefix_template', e.target.value)}
                                helperText="Variables: {YYYY} = FY (e.g., 2526)"
                                fullWidth
                            />

                            <TextField
                                label="Current Prefix"
                                value={settings?.current_prefix || ''}
                                helperText="Auto-calculated from template"
                                disabled
                                fullWidth
                            />

                            <TextField
                                label="Next Sequence Number"
                                type="number"
                                value={formData.next_sequence}
                                onChange={(e) => handleChange('next_sequence', parseInt(e.target.value) || 1)}
                                helperText="The next SO will use this sequence number"
                                fullWidth
                            />

                            <TextField
                                label="Padding"
                                type="number"
                                value={formData.padding}
                                onChange={(e) => handleChange('padding', parseInt(e.target.value) || 1)}
                                helperText="Number of digits (e.g., 5 = 00001)"
                                fullWidth
                            />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle1">Auto Reset Yearly</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Reset numbering to 1 at start of financial year
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.auto_reset_yearly}
                                            onChange={(e) => handleChange('auto_reset_yearly', e.target.checked)}
                                        />
                                    }
                                    label={formData.auto_reset_yearly ? 'Enabled' : 'Disabled'}
                                />
                            </Box>

                            <TextField
                                label="Preview Next Number"
                                value={`${settings?.current_prefix}${String(formData.next_sequence).padStart(formData.padding, '0')}`}
                                disabled
                                fullWidth
                            />

                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                sx={{ mt: 2 }}
                            >
                                {saving ? 'Saving...' : 'Update Settings'}
                            </Button>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Template Variables
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2">
                                <strong>{'{YYYY}'}</strong> = Financial Year (2526)
                            </Typography>
                            <Typography variant="body2">
                                <strong>{'{YY}'}</strong> = Year (25)
                            </Typography>
                            <Typography variant="body2">
                                <strong>{'{MM}'}</strong> = Month (12)
                            </Typography>
                            <Typography variant="body2">
                                <strong>{'{DD}'}</strong> = Day (14)
                            </Typography>
                        </Box>

                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Changing the next sequence number will affect all new sales orders.
                        </Alert>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SequenceManagement;
